import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';

import { useCreateOfferBypassRegionFilterSetting } from '~/hooks/apis/promo/useCreateOfferBypassRegionFilterSetting';
import { useCreateOfferRegionFilterExclusion } from '~/hooks/apis/promo/useCreateOfferRegionFilterExclusion';
import { useDeleteOfferBypassRegionFilterSetting } from '~/hooks/apis/promo/useDeleteOfferBypassRegionFilterSetting';
import { useDeleteOfferRegionFilterExclusion } from '~/hooks/apis/promo/useDeleteOfferRegionFilterExclusion';
import { useListOfferBypassRegionFilterSettings } from '~/hooks/apis/promo/useListOfferBypassRegionFilterSettings';
import { useListOfferRegionFilterExclusions } from '~/hooks/apis/promo/useListOfferRegionFilterExclusions';
import { useUpdateOfferBypassRegionFilterSetting } from '~/hooks/apis/promo/useUpdateOfferBypassRegionFilterSetting';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import useDebounced from '~/hooks/useDebounced';

import type {
  CreateOfferBypassRegionFilterSettingPayload,
  OfferBypassRegionFilterSetting,
  OfferRegionFilterExclusion,
} from '~/services/PromoService';
import { customerPortalHost } from '~/services/common';

import DateFormatterDetailed from '~/utils/dateFormatterDetails';

// ─── Bypass filter settings helpers ──────────────────────────────────────────

type StatusFilter = 'active' | 'expired' | 'all';
type SettingFormFields = CreateOfferBypassRegionFilterSettingPayload;

function isActive(setting: OfferBypassRegionFilterSetting): boolean {
  if (!setting.expires_at) return true;
  return new Date(setting.expires_at) > new Date();
}

function settingToForm(setting: OfferBypassRegionFilterSetting): SettingFormFields {
  return {
    source: setting.source ?? '',
    medium: setting.medium ?? '',
    campaign: setting.campaign ?? '',
    term: setting.term ?? '',
    start_date: setting.start_date ?? '',
    end_date: setting.end_date ?? '',
  };
}

const emptySettingForm: SettingFormFields = {
  source: '',
  medium: '',
  campaign: '',
  term: '',
  start_date: '',
  end_date: '',
};

function stripEmpty(form: SettingFormFields): Partial<SettingFormFields> {
  return Object.fromEntries(
    Object.entries(form).filter(([, v]) => v !== '' && v !== undefined),
  ) as Partial<SettingFormFields>;
}

function utmLabel(setting: OfferBypassRegionFilterSetting): string {
  return [setting.source, setting.medium, setting.campaign, setting.term].filter(Boolean).join(' / ') || '—';
}

// ─── Bypass filter settings tab ──────────────────────────────────────────────

function BypassFilterSettingsTab() {
  const { enqueueSnackbar } = useSnackbar();
  const { tenant } = useCurrentTenant();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState<SettingFormFields>(emptySettingForm);

  const [editTarget, setEditTarget] = useState<OfferBypassRegionFilterSetting | null>(null);
  const [editForm, setEditForm] = useState<SettingFormFields>(emptySettingForm);

  const [deleteTarget, setDeleteTarget] = useState<OfferBypassRegionFilterSetting | null>(null);

  const listQuery = useMemo(
    () => ({ page, pageSize: rowsPerPage, status: statusFilter }),
    [page, rowsPerPage, statusFilter],
  );

  const { data, isLoading, isError, error } = useListOfferBypassRegionFilterSettings({ query: listQuery });
  const settings = data?.result.offerBypassRegionFilterSettingList ?? [];
  const total = data?.result.total ?? 0;

  const createMutation = useCreateOfferBypassRegionFilterSetting();
  const updateMutation = useUpdateOfferBypassRegionFilterSetting();
  const deleteMutation = useDeleteOfferBypassRegionFilterSetting();

  function setCreateField(field: keyof SettingFormFields, value: string) {
    setCreateForm((prev) => ({ ...prev, [field]: value }));
  }

  function setEditField(field: keyof SettingFormFields, value: string) {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleCreateSubmit() {
    const payload = stripEmpty(createForm);
    if (!payload.source && !payload.medium && !payload.campaign && !payload.term) {
      enqueueSnackbar('At least one UTM parameter (source, medium, campaign, or term) is required', {
        variant: 'warning',
      });
      return;
    }
    try {
      await createMutation.mutateAsync(payload);
      enqueueSnackbar('Setting created', { variant: 'success' });
      setCreateDialogOpen(false);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Failed to create setting', { variant: 'error' });
    }
  }

  async function handleEditSubmit() {
    if (!editTarget) return;
    try {
      await updateMutation.mutateAsync({
        id: editTarget.offer_bypass_region_filter_setting_id,
        payload: stripEmpty(editForm),
      });
      enqueueSnackbar('Setting updated', { variant: 'success' });
      setEditTarget(null);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Failed to update setting', { variant: 'error' });
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.offer_bypass_region_filter_setting_id);
      enqueueSnackbar('Setting expired', { variant: 'success' });
      setDeleteTarget(null);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Failed to expire setting', { variant: 'error' });
    }
  }

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setCreateForm(emptySettingForm);
            setCreateDialogOpen(true);
          }}
        >
          Add setting
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="flex-end">
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="obrf-status-filter">Status</InputLabel>
          <Select
            labelId="obrf-status-filter"
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as StatusFilter);
              setPage(0);
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {isLoading && <Spinner />}
      {isError && (
        <Typography color="error">{error instanceof Error ? error.message : 'Failed to load settings'}</Typography>
      )}

      {!isLoading && !isError && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 80 }}>ID</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Medium</TableCell>
                <TableCell>Campaign</TableCell>
                <TableCell>Term</TableCell>
                <TableCell>Link</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Start / End</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {settings.map((row) => (
                <TableRow key={row.offer_bypass_region_filter_setting_id} hover>
                  <TableCell sx={{ width: 80 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography sx={{ fontFamily: 'monospace', fontSize: 11 }} noWrap>
                        {row.offer_bypass_region_filter_setting_id.slice(0, 8)}…
                      </Typography>
                      <Tooltip title="Copy ID">
                        <IconButton
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(row.offer_bypass_region_filter_setting_id);
                            enqueueSnackbar('ID copied', { variant: 'info' });
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell>{row.source ?? '—'}</TableCell>
                  <TableCell>{row.medium ?? '—'}</TableCell>
                  <TableCell>{row.campaign ?? '—'}</TableCell>
                  <TableCell>{row.term ?? '—'}</TableCell>
                  <TableCell>
                    <Link
                      href={`${customerPortalHost(tenant)}?${qs.stringify({
                        utm_source: row.source,
                        utm_medium: row.medium,
                        utm_campaign: row.campaign,
                        ...(row.term ? { utm_term: row.term } : {}),
                      })}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Customer Portal Link
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={isActive(row) ? 'Active' : 'Expired'}
                      color={isActive(row) ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography variant="body2">
                        <b>Start:</b> {row.start_date ? <DateFormatterDetailed date={row.start_date} /> : '—'}
                      </Typography>
                      <Typography variant="body2">
                        <b>End:</b>{' '}
                        {row.end_date ? <DateFormatterDetailed date={row.end_date} highlightIfPast={true} /> : '—'}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <DateFormatterDetailed date={row.created_at} />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {isActive(row) && (
                        <>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => {
                              setEditTarget(row);
                              setEditForm(settingToForm(row));
                            }}
                          >
                            Edit
                          </Button>
                          <Button size="small" variant="text" color="error" onClick={() => setDeleteTarget(row)}>
                            Expire
                          </Button>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {settings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography color="text.secondary">No settings match the current filters.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number.parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}

      {/* Create dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Bypass Filter Setting</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Source"
              value={createForm.source ?? ''}
              onChange={(e) => setCreateField('source', e.target.value)}
              fullWidth
              autoFocus
              helperText="UTM source (e.g. google)"
            />
            <TextField
              label="Medium"
              value={createForm.medium ?? ''}
              onChange={(e) => setCreateField('medium', e.target.value)}
              fullWidth
              helperText="UTM medium (e.g. cpc)"
            />
            <TextField
              label="Campaign"
              value={createForm.campaign ?? ''}
              onChange={(e) => setCreateField('campaign', e.target.value)}
              fullWidth
              helperText="UTM campaign"
            />
            <TextField
              label="Term"
              value={createForm.term ?? ''}
              onChange={(e) => setCreateField('term', e.target.value)}
              fullWidth
              helperText="UTM term"
            />
            <TextField
              label="Start date"
              type="datetime-local"
              value={createForm.start_date ?? ''}
              onChange={(e) => setCreateField('start_date', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End date"
              type="datetime-local"
              value={createForm.end_date ?? ''}
              onChange={(e) => setCreateField('end_date', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setCreateDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleCreateSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Bypass Filter Setting</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Source"
              value={editForm.source ?? ''}
              onChange={(e) => setEditField('source', e.target.value)}
              fullWidth
              autoFocus
              helperText="UTM source (e.g. google)"
            />
            <TextField
              label="Medium"
              value={editForm.medium ?? ''}
              onChange={(e) => setEditField('medium', e.target.value)}
              fullWidth
              helperText="UTM medium (e.g. cpc)"
            />
            <TextField
              label="Campaign"
              value={editForm.campaign ?? ''}
              onChange={(e) => setEditField('campaign', e.target.value)}
              fullWidth
              helperText="UTM campaign"
            />
            <TextField
              label="Term"
              value={editForm.term ?? ''}
              onChange={(e) => setEditField('term', e.target.value)}
              fullWidth
              helperText="UTM term"
            />
            <TextField
              label="Start date"
              type="datetime-local"
              value={editForm.start_date ?? ''}
              onChange={(e) => setEditField('start_date', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End date"
              type="datetime-local"
              value={editForm.end_date ?? ''}
              onChange={(e) => setEditField('end_date', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setEditTarget(null)}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleEditSubmit} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Expire confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Expire setting?</DialogTitle>
        <DialogContent>
          <Typography>
            This will expire the bypass filter for <strong>{deleteTarget ? utmLabel(deleteTarget) : ''}</strong>. Region
            filtering will resume for this traffic.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? 'Expiring…' : 'Expire'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Offer exclusions tab ─────────────────────────────────────────────────────

function OfferExclusionsTab() {
  const { enqueueSnackbar } = useSnackbar();

  const [offerIdFilter, setOfferIdFilter] = useState('');
  const debouncedOfferIdFilter = useDebounced(offerIdFilter, 300);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [offerIdInput, setOfferIdInput] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<OfferRegionFilterExclusion | null>(null);

  const listQuery = useMemo(
    () => ({
      page,
      pageSize: rowsPerPage,
      ...(debouncedOfferIdFilter.trim() ? { offer_id: debouncedOfferIdFilter.trim() } : {}),
    }),
    [page, rowsPerPage, debouncedOfferIdFilter],
  );

  const { data, isLoading, isError, error } = useListOfferRegionFilterExclusions({ query: listQuery });
  const exclusions = data?.result.offerRegionFilterExclusionList ?? [];
  const total = data?.result.total ?? 0;

  const createMutation = useCreateOfferRegionFilterExclusion();
  const deleteMutation = useDeleteOfferRegionFilterExclusion();

  async function handleCreateSubmit() {
    if (!offerIdInput.trim()) {
      enqueueSnackbar('Offer ID is required', { variant: 'warning' });
      return;
    }
    try {
      await createMutation.mutateAsync({ offer_id: offerIdInput.trim() });
      enqueueSnackbar('Offer added to exclusion list', { variant: 'success' });
      setCreateDialogOpen(false);
      setOfferIdInput('');
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Failed to add exclusion', { variant: 'error' });
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.offer_region_filter_exclusion_id);
      enqueueSnackbar('Offer removed from exclusion list', { variant: 'success' });
      setDeleteTarget(null);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Failed to remove exclusion', { variant: 'error' });
    }
  }

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setOfferIdInput('');
            setCreateDialogOpen(true);
          }}
        >
          Add offer
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="flex-end">
        <TextField
          label="Filter by Offer ID"
          value={offerIdFilter}
          onChange={(e) => {
            setOfferIdFilter(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ minWidth: 300 }}
        />
      </Stack>

      {isLoading && <Spinner />}
      {isError && (
        <Typography color="error">{error instanceof Error ? error.message : 'Failed to load exclusions'}</Typography>
      )}

      {!isLoading && !isError && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Offer ID</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {exclusions.map((row) => (
                <TableRow key={row.offer_region_filter_exclusion_id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {row.offer_region_filter_exclusion_id}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{row.offer_id}</TableCell>
                  <TableCell>
                    <DateFormatterDetailed date={row.created_at} />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.created_by}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="text" color="error" onClick={() => setDeleteTarget(row)}>
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {exclusions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary">No offer exclusions found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number.parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}

      {/* Create dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Offer to Exclusion List</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              label="Offer ID"
              value={offerIdInput}
              onChange={(e) => setOfferIdInput(e.target.value)}
              fullWidth
              required
              autoFocus
              helperText="This offer will bypass region filtering globally."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setCreateDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateSubmit}
            disabled={!offerIdInput.trim() || createMutation.isPending}
          >
            {createMutation.isPending ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove offer exclusion?</DialogTitle>
        <DialogContent>
          <Typography>
            Remove offer <strong>{deleteTarget?.offer_id}</strong> from the exclusion list? Region filtering will apply
            to this offer again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type InnerTab = 'settings' | 'exclusions';

export default function OfferBypassRegionFilterPage() {
  const [innerTab, setInnerTab] = useState<InnerTab>('settings');

  return (
    <>
      <Helmet>
        <title>Marketing / Offer Region Bypass Filter</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <PageHeader title="Offer Region Bypass Filter" />

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={innerTab} onChange={(_, v) => setInnerTab(v)}>
            <Tab label="UTM Channel Settings" value="settings" />
            <Tab label="Offer Exclusions" value="exclusions" />
          </Tabs>
        </Box>

        {innerTab === 'settings' && <BypassFilterSettingsTab />}
        {innerTab === 'exclusions' && <OfferExclusionsTab />}
      </Container>
    </>
  );
}
